import apiClient from './api';

/**
 * User/Patient Service
 * Handles user and patient-related API operations
 */

class UserService {
  /**
   * Get all patients
   * @param {Object} filters - Optional filters (isActive, search)
   * @returns {Promise} List of patients
   */
  async getPatients(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.isActive !== undefined) {
        params.append('isActive', filters.isActive);
      }
      
      if (filters.search) {
        params.append('search', filters.search);
      }

      const queryString = params.toString();
      const url = `/api/users/patients${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiClient.get(url);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to fetch patients',
      };
    }
  }

  /**
   * Get patient by ID
   * @param {string} patientId - Patient ID
   * @returns {Promise} Patient details
   */
  async getPatientById(patientId) {
    try {
      const response = await apiClient.get(`/api/users/patients/${patientId}`);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to fetch patient details',
      };
    }
  }

  /**
   * Get all users (admin only)
   * @param {Object} filters - Optional filters (role, isActive, page, limit, search, status, risk, sortField, sortDirection)
   * @returns {Promise} List of users with pagination
   */
  async getAllUsers(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.role) {
        params.append('role', filters.role);
      }
      
      if (filters.isActive !== undefined) {
        params.append('isActive', filters.isActive);
      }
      
      if (filters.page) {
        params.append('page', filters.page);
      }
      
      if (filters.limit) {
        params.append('limit', filters.limit);
      }

      if (filters.search) {
        params.append('search', filters.search);
      }

      if (filters.status) {
        params.append('status', filters.status);
      }

      if (filters.risk) {
        params.append('risk', filters.risk);
      }

      if (filters.sortField) {
        params.append('sortField', filters.sortField);
      }

      if (filters.sortDirection) {
        params.append('sortDirection', filters.sortDirection);
      }

      const queryString = params.toString();
      const url = `/api/users/all${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiClient.get(url);
      
      return {
        success: true,
        data: response.data.data.users || response.data.data,
        total: response.data.data.total,
        totalPages: response.data.data.totalPages,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to fetch users',
      };
    }
  }

  /**
   * Get users with their latest scan (single optimized call)
   */
  async getUsersWithLatestScan(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.role) params.append('role', filters.role);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.risk) params.append('risk', filters.risk);
      if (filters.sortField) params.append('sortField', filters.sortField);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

      const queryString = params.toString();
      const url = `/api/users/with-latest-scan${queryString ? `?${queryString}` : ''}`;
      const response = await apiClient.get(url);
      const data = response.data.data;
      return {
        success: true,
        data: data.users,
        total: data.total,
        totalPages: data.pages,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to fetch users with latest scan',
      };
    }
  }

  /**
   * Create a new user (admin only)
   * @param {Object} userData - User data (username, email, password, role, etc.)
   * @returns {Promise} Created user
   */
  async createUser(userData) {
    try {
      const response = await apiClient.post('/api/users/create', userData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to create user',
        errors: error.errors,
      };
    }
  }

  /**
   * Update user (admin only)
   * @param {string} userId - User ID
   * @param {Object} updateData - User data to update
   * @returns {Promise} Updated user
   */
  async updateUser(userId, updateData) {
    try {
      const response = await apiClient.put(`/api/users/${userId}`, updateData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to update user',
        errors: error.errors,
      };
    }
  }

  /**
   * Reset user password (admin only)
   * @param {string} userId - User ID
   * @param {string} newPassword - New password
   * @returns {Promise} Success response
   */
  async resetUserPassword(userId, newPassword) {
    try {
      const response = await apiClient.post(`/api/users/${userId}/reset-password`, {
        newPassword,
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to reset password',
        errors: error.errors,
      };
    }
  }

  /**
   * Deactivate user (admin only)
   * @param {string} userId - User ID
   * @returns {Promise} Success response
   */
  async deactivateUser(userId) {
    try {
      const response = await apiClient.post(`/api/users/${userId}/deactivate`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to deactivate user',
      };
    }
  }

  /**
   * Activate user (admin only)
   * @param {string} userId - User ID
   * @returns {Promise} Success response
   */
  async activateUser(userId) {
    try {
      const response = await apiClient.post(`/api/users/${userId}/activate`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      throw {
        success: false,
        message: error.message || 'Failed to activate user',
      };
    }
  }
}

export default new UserService();

